import { Container, createTheme, CssBaseline, ThemeProvider } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation, useParams } from 'react-router-dom';
import api from './api';
import Header from './components/Header';
import ScrollToTop from './components/ScrollToTop';
import Sidebar from "./components/Sidebar";
import CalculatorPage from "./pages/calculator/CalculatorPage";
import TutorialsPage from "./pages/tutorials/TutorialsPage";
import TutorialVideosAdminPage from './pages/tutorial-videos/TutorialVideosAdminPage';
import { EditTutorialVideoPage, RegisterTutorialVideoPage } from './pages/tutorial-videos/TutorialVideoFormPage';
import EditCheckoutPage from "./pages/checkouts/EditCheckoutPage";
import QuickCheckoutPage from "./pages/checkouts/QuickCheckoutPage";
import RegisterCheckoutPage from "./pages/checkouts/RegisterCheckoutPage";
import RegisterBatchCheckoutPage from "./pages/checkouts/RegisterBatchCheckoutPage";
import QuotesPage from './pages/quotes/QuotesPage';
import RegisterQuotePage from './pages/quotes/RegisterQuotePage';
import EditQuotePage from './pages/quotes/EditQuotePage';
import PrintQuotePage from './pages/quotes/PrintQuotePage';
import EditCustomerPage from "./pages/customers/EditCustomerPage";
import ListCustomersPage from "./pages/customers/ListCustomersPage";
import RegisterCustomerPage from "./pages/customers/RegisterCustomerPage";
import DashboardPage from './pages/DashboardPage';
import ForgotPassword from "./pages/ForgotPassword";
import AccountConfirmation from "./pages/front/AccountConfirmation";
import HomePage from "./pages/front/HomePage";
import PasswordRedefinition from "./pages/front/PasswordRedefinition";
import PrivacyPolicyPage from "./pages/front/PrivacyPolicyPage";
import TermsOfUsePage from "./pages/front/TermsOfUsePage";
import CalculatorPageFront from "./pages/front/PublicCalculatorPage";
import LoginPage from './pages/LoginPage';
import EditMaterialPage from "./pages/materials/EditMaterialPage";
import ListMaterialsPage from './pages/materials/ListMaterialsPage';
import RegisterMaterialPage from "./pages/materials/RegisterMaterialPage";
import MyAccountPage from "./pages/my-account/MyAccountPage";
import MySubscriptionPage from "./pages/my-account/MySubscriptionPage";
import SubscriptionReturnPage from "./pages/my-account/SubscriptionReturnPage";
import SalesChannelsPage from "./pages/my-account/SalesChannelsPage";
import PaymentFeesPage from "./pages/my-account/PaymentFeesPage";
import IntegrationsPage from "./pages/integrations/IntegrationsPage";
import CreateNotificationPage from './pages/notifications/CreateNotificationPage';
import FeatureFlagsAdminPage from './pages/feature-flags/FeatureFlagsAdminPage';
import ListUsersPage from './pages/users/ListUsersPage';
import UserAssetsPage from './pages/users/UserAssetsPage';
import { EditUserPage, RegisterUserPage } from './pages/users/UserFormPage';
import DesktopReleasesAdminPage from './pages/desktop-releases/DesktopReleasesAdminPage';
import { EditDesktopReleasePage, NewDesktopReleasePage } from './pages/desktop-releases/DesktopReleaseFormPage';
import FarmLinkChangelogPage from './pages/desktop-releases/FarmLinkChangelogPage';
import ListPrinterModelsPage from './pages/printer-models/ListPrinterModelsPage';
import { EditPrinterModelPage, NewPrinterModelPage } from './pages/printer-models/PrinterModelFormPage';
import ListSubscriptionPlansPage from './pages/subscription-plans/ListSubscriptionPlansPage';
import { EditSubscriptionPlanPage, NewSubscriptionPlanPage } from './pages/subscription-plans/SubscriptionPlanFormPage';
import TicketsListPage from './pages/tickets/TicketsListPage';
import NewTicketPage from './pages/tickets/NewTicketPage';
import TicketDetailPage from './pages/tickets/TicketDetailPage';
import MpSubscriptionsListPage from './pages/mp-subscriptions/MpSubscriptionsListPage';
import SubscriptionBillingExemptionsPage from './pages/subscription-billing/SubscriptionBillingExemptionsPage';
import PartnersListPage from './pages/partners/PartnersListPage';
import PartnerFormPage from './pages/partners/PartnerFormPage';
import PartnerDetailPage from './pages/partners/PartnerDetailPage';
import PartnersDashboardPage from './pages/partners/PartnersDashboardPage';
import PartnersCalendarPage from './pages/partners/PartnersCalendarPage';
import PartnersMapPage from './pages/partners/PartnersMapPage';
import PartnerVisitPage from './pages/partners/PartnerVisitPage';
import PartnerVisitReceiptPage from './pages/partners/PartnerVisitReceiptPage';
import OrderDetailsPage from "./pages/orders/OrderDetailsPage";
import OrdersPage from "./pages/orders/OrdersPage";
import OrdersKanbanPage from "./pages/orders/OrdersKanbanPage";
import EditJobPage from "./pages/jobs/EditJobPage";
import ListJobsPage from "./pages/jobs/ListJobsPage";
import RegisterJobPage from "./pages/jobs/RegisterJobPage";
import EditPrinterMaintenancePage from "./pages/printer-maintenances/EditPrinterMaintenancePage";
import ListPrinterMaintenancesPage from "./pages/printer-maintenances/ListPrinterMaintenancesPage";
import RegisterPrinterMaintenancePage from "./pages/printer-maintenances/RegisterPrinterMaintenancePage";
import ListReplacementPartsPage from "./pages/replacement-parts/ListReplacementPartsPage";
import { EditReplacementPartPage, RegisterReplacementPartPage } from "./pages/replacement-parts/ReplacementPartFormPage";
import EditPrinterPage from "./pages/printers/EditPrinterPage";
import ListPrintersPage from './pages/printers/ListPrintersPage';
import RegisterPrinterPage from "./pages/printers/RegisterPrinterPage";
import EditProductPage from "./pages/products/EditProductPage";
import ProductsPage from './pages/products/ProductsPage';
import RegisterProductPage from "./pages/products/RegisterProductPage";
import CreateTransactionPage from './pages/transactions/CreateTransactionPage';
import EditTransactionPage from './pages/transactions/EditTransactionPage';
import TransactionsPage from './pages/transactions/TransactionsPage';
import SettlementImportPage from './pages/transactions/SettlementImportPage';
import ListOperationalCostsPage from './pages/operational-costs/ListOperationalCostsPage';
import { EditOperationalCostPage, RegisterOperationalCostPage } from './pages/operational-costs/OperationalCostFormPage';
import PaywallPage from './pages/plans/PaywallPage';
import { refreshSessionContext } from './helpers/sessionContext';
import { hasPremiumAccess, hasProAccess, readStoredPlanName } from './helpers/subscription';
import { isMasterUser } from './helpers/authSession';
import { useFeatureFlag } from './helpers/featureFlags';
import { buildPaywallUrl } from './helpers/paywall';
import { persistDarkModePreference, readDarkModePreference } from './helpers/themePreference';
import { bindCaptureRuntime, persistCaptureModePreference, readCaptureModePreference } from './helpers/captureMode';
import useLiveNotifications from './hooks/useLiveNotifications';

function CheckoutEditRedirect() {
    const { id } = useParams();
    return <Navigate to={`/admin/orders/${id}/edit`} replace />;
}

function PremiumRoute({ children }) {
    const accessToken = localStorage.getItem('access_token');
    const location = useLocation();
    if (!accessToken) {
        return <Navigate to="/admin/login" />;
    }
    if (!hasPremiumAccess(readStoredPlanName())) {
        return <Navigate to={buildPaywallUrl({ returnTo: location.pathname })} replace />;
    }
    return children;
}

function FarmLinkRoute({ children }) {
    const accessToken = localStorage.getItem('access_token');
    const location = useLocation();
    if (!accessToken) {
        return <Navigate to="/admin/login" />;
    }
    if (!isMasterUser() && !hasProAccess(readStoredPlanName())) {
        return (
            <Navigate
                to={buildPaywallUrl({ returnTo: location.pathname, featureKey: 'integrations' })}
                replace
            />
        );
    }
    return children;
}

function FeatureFlagRoute({ featureKey, children }) {
    const accessToken = localStorage.getItem('access_token');
    const location = useLocation();
    const { enabled, loading } = useFeatureFlag(featureKey);
    if (!accessToken) {
        return <Navigate to="/admin/login" />;
    }
    if (loading) {
        return null;
    }
    if (!enabled && !isMasterUser()) {
        return (
            <Navigate
                to={buildPaywallUrl({ returnTo: location.pathname, featureKey })}
                replace
            />
        );
    }
    return children;
}
function PrivateRoute({ children }) {
    const accessToken = localStorage.getItem('access_token');
    return accessToken ? children : <Navigate to="/admin/login" />;
}

// Rotas administrativas encapsuladas no caminho "/admin"
function AdminRoutes() {
    const [sidebarOpen, setSidebarOpen] = useState(false); // Controle da visibilidade da Sidebar
    const [darkMode, setDarkMode] = useState(readDarkModePreference);
    const [captureMode, setCaptureMode] = useState(readCaptureModePreference);

    const location = useLocation();
    const isLoginPage = location.pathname === '/admin/login';
    const isForgotPasswordPage = location.pathname === '/admin/forgot-password';
    const isPublicAuthPage = isLoginPage || isForgotPasswordPage;
    const liveNotificationsEnabled = !isPublicAuthPage && Boolean(localStorage.getItem('access_token'));

    useLiveNotifications(liveNotificationsEnabled);

    useEffect(() => {
        if (isPublicAuthPage || !localStorage.getItem('access_token')) {
            return undefined;
        }
        refreshSessionContext().catch(() => {});
        return undefined;
    }, [isPublicAuthPage]);

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen); // Alterna entre aberto e fechado
    };

    const toggleDarkMode = () => {
        const newDarkMode = !darkMode;
        setDarkMode(newDarkMode);
        persistDarkModePreference(newDarkMode);
    };

    const toggleCaptureMode = () => {
        const next = !captureMode;
        setCaptureMode(next);
        persistCaptureModePreference(next);
    };

    bindCaptureRuntime(captureMode && !isPublicAuthPage, location.pathname);

    // Cria o tema dinâmico com base no estado do darkMode
    const theme = createTheme({
        palette: {
            mode: (darkMode && !isPublicAuthPage) ? 'dark' : 'light',
        },
    });

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            {!isLoginPage && !isForgotPasswordPage &&
                <Header
                    toggleSidebar={toggleSidebar}
                    toggleDarkMode={toggleDarkMode}
                    darkMode={darkMode}
                    captureMode={captureMode}
                    toggleCaptureMode={toggleCaptureMode}
                />}
            <Sidebar open={sidebarOpen} onClose={toggleSidebar} darkMode={darkMode} />
            <div style={{ display: 'flex' }}>
                <Container component="main" style={{ flexGrow: 1, padding: '20px' }}>
                    <Routes>
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route
                            path="/"
                            element={
                                <PrivateRoute>
                                    <DashboardPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/my-account"
                            element={
                                <PrivateRoute>
                                    <MyAccountPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/my-account/subscription/return"
                            element={
                                <PrivateRoute>
                                    <SubscriptionReturnPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/my-account/subscription"
                            element={
                                <PrivateRoute>
                                    <MySubscriptionPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/plans"
                            element={
                                <PrivateRoute>
                                    <PaywallPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/sales-channels"
                            element={
                                <PrivateRoute>
                                    <SalesChannelsPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/payment-fees"
                            element={
                                <PrivateRoute>
                                    <PaymentFeesPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/integrations"
                            element={
                                <PrivateRoute>
                                    <FeatureFlagRoute featureKey="integrations">
                                        <IntegrationsPage />
                                    </FeatureFlagRoute>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/printers"
                            element={
                                <PrivateRoute>
                                    <ListPrintersPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/register-printer"
                            element={
                                <PrivateRoute>
                                    <RegisterPrinterPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/edit-printer/:id"
                            element={
                                <PrivateRoute>
                                    <EditPrinterPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/jobs"
                            element={
                                <PrivateRoute>
                                    <ListJobsPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/register-job"
                            element={
                                <PrivateRoute>
                                    <RegisterJobPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/edit-job/:id"
                            element={
                                <PrivateRoute>
                                    <EditJobPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/printer-maintenances"
                            element={
                                <PrivateRoute>
                                    <ListPrinterMaintenancesPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/register-printer-maintenance"
                            element={
                                <PrivateRoute>
                                    <RegisterPrinterMaintenancePage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/edit-printer-maintenance/:id"
                            element={
                                <PrivateRoute>
                                    <EditPrinterMaintenancePage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/replacement-parts"
                            element={
                                <PrivateRoute>
                                    <ListReplacementPartsPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/register-replacement-part"
                            element={
                                <PrivateRoute>
                                    <RegisterReplacementPartPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/edit-replacement-part/:id"
                            element={
                                <PrivateRoute>
                                    <EditReplacementPartPage />
                                </PrivateRoute>
                            }
                        />

                        <Route
                            path="/customers"
                            element={
                                <PrivateRoute>
                                    <ListCustomersPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/register-customer"
                            element={
                                <PrivateRoute>
                                    <RegisterCustomerPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/edit-customer/:id"
                            element={
                                <PrivateRoute>
                                    <EditCustomerPage />
                                </PrivateRoute>
                            }
                        />

                        <Route
                            path="/materials"
                            element={
                                <PrivateRoute>
                                    <ListMaterialsPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/register-material"
                            element={
                                <PrivateRoute>
                                    <RegisterMaterialPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/edit-material/:id"
                            element={
                                <PrivateRoute>
                                    <EditMaterialPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/products"
                            element={
                                <PrivateRoute>
                                    <ProductsPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/register-product"
                            element={
                                <PrivateRoute>
                                    <RegisterProductPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/edit-product/:id"
                            element={
                                <PrivateRoute>
                                    <EditProductPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/quotes"
                            element={
                                <FeatureFlagRoute featureKey="quotes">
                                    <QuotesPage />
                                </FeatureFlagRoute>
                            }
                        />
                        <Route
                            path="/register-quote"
                            element={
                                <FeatureFlagRoute featureKey="quotes">
                                    <RegisterQuotePage />
                                </FeatureFlagRoute>
                            }
                        />
                        <Route
                            path="/edit-quote/:id"
                            element={
                                <FeatureFlagRoute featureKey="quotes">
                                    <EditQuotePage />
                                </FeatureFlagRoute>
                            }
                        />
                        <Route
                            path="/quotes/:id/print"
                            element={
                                <FeatureFlagRoute featureKey="quotes">
                                    <PrintQuotePage />
                                </FeatureFlagRoute>
                            }
                        />
                        <Route path="/checkouts" element={<Navigate to="/admin/orders" replace />} />
                        <Route path="/register-checkout" element={<Navigate to="/admin/orders/new" replace />} />
                        <Route path="/register-batch-checkout" element={<Navigate to="/admin/orders/batch" replace />} />
                        <Route path="/edit-checkout/:id" element={<CheckoutEditRedirect />} />
                        <Route
                            path="/orders/new"
                            element={
                                <PrivateRoute>
                                    <RegisterCheckoutPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/orders/batch"
                            element={
                                <PrivateRoute>
                                    <PremiumRoute>
                                        <RegisterBatchCheckoutPage />
                                    </PremiumRoute>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/orders/:id/edit"
                            element={
                                <PrivateRoute>
                                    <EditCheckoutPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/quick-checkout"
                            element={
                                <PrivateRoute>
                                    <FeatureFlagRoute featureKey="quick_checkout">
                                        <QuickCheckoutPage />
                                    </FeatureFlagRoute>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/orders"
                            element={
                                <PrivateRoute>
                                    <OrdersKanbanPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/orders/list"
                            element={
                                <PrivateRoute>
                                    <OrdersPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/orders/:id"
                            element={
                                <PrivateRoute>
                                    <OrderDetailsPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/transactions"
                            element={
                                <PrivateRoute>
                                    <FeatureFlagRoute featureKey="finance">
                                        <TransactionsPage />
                                    </FeatureFlagRoute>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/operational-costs"
                            element={
                                <PrivateRoute>
                                    <FeatureFlagRoute featureKey="operational_costs">
                                        <ListOperationalCostsPage />
                                    </FeatureFlagRoute>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/register-operational-cost"
                            element={
                                <PrivateRoute>
                                    <FeatureFlagRoute featureKey="operational_costs">
                                        <RegisterOperationalCostPage />
                                    </FeatureFlagRoute>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/edit-operational-cost/:id"
                            element={
                                <PrivateRoute>
                                    <FeatureFlagRoute featureKey="operational_costs">
                                        <EditOperationalCostPage />
                                    </FeatureFlagRoute>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/settlement-import"
                            element={
                                <PrivateRoute>
                                    <FeatureFlagRoute featureKey="bank_reconciliation">
                                        <SettlementImportPage />
                                    </FeatureFlagRoute>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/create-transaction"
                            element={
                                <PrivateRoute>
                                    <FeatureFlagRoute featureKey="finance">
                                        <CreateTransactionPage />
                                    </FeatureFlagRoute>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/edit-transaction/:transactionId"
                            element={
                                <PrivateRoute>
                                    <FeatureFlagRoute featureKey="finance">
                                        <EditTransactionPage />
                                    </FeatureFlagRoute>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/tutorials"
                            element={
                                <PrivateRoute>
                                    <TutorialsPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/calculator"
                            element={
                                <PrivateRoute>
                                    <CalculatorPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/notifications"
                            element={
                                <PrivateRoute>
                                    <CreateNotificationPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/feature-flags"
                            element={
                                <PrivateRoute>
                                    <FeatureFlagsAdminPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/partners"
                            element={
                                <PrivateRoute>
                                    <FeatureFlagRoute featureKey="commerce_partners">
                                        <PartnersDashboardPage />
                                    </FeatureFlagRoute>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/partners/dashboard"
                            element={<Navigate to="/admin/partners" replace />}
                        />
                        <Route
                            path="/partners/list"
                            element={
                                <PrivateRoute>
                                    <FeatureFlagRoute featureKey="commerce_partners">
                                        <PartnersListPage />
                                    </FeatureFlagRoute>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/partners/calendar"
                            element={
                                <PrivateRoute>
                                    <FeatureFlagRoute featureKey="commerce_partners">
                                        <PartnersCalendarPage />
                                    </FeatureFlagRoute>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/partners/map"
                            element={
                                <PrivateRoute>
                                    <FeatureFlagRoute featureKey="commerce_partners">
                                        <PartnersMapPage />
                                    </FeatureFlagRoute>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/partners/new"
                            element={
                                <PrivateRoute>
                                    <FeatureFlagRoute featureKey="commerce_partners">
                                        <PartnerFormPage />
                                    </FeatureFlagRoute>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/partners/:uuid/edit"
                            element={
                                <PrivateRoute>
                                    <FeatureFlagRoute featureKey="commerce_partners">
                                        <PartnerFormPage />
                                    </FeatureFlagRoute>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/partners/visits/:visitUuid"
                            element={
                                <PrivateRoute>
                                    <FeatureFlagRoute featureKey="commerce_partners">
                                        <PartnerVisitPage />
                                    </FeatureFlagRoute>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/partners/visits/:visitUuid/receipt"
                            element={
                                <PrivateRoute>
                                    <FeatureFlagRoute featureKey="commerce_partners">
                                        <PartnerVisitReceiptPage />
                                    </FeatureFlagRoute>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/partners/:uuid"
                            element={
                                <PrivateRoute>
                                    <FeatureFlagRoute featureKey="commerce_partners">
                                        <PartnerDetailPage />
                                    </FeatureFlagRoute>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/users"
                            element={
                                <PrivateRoute>
                                    <ListUsersPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/users/:uuid/assets"
                            element={
                                <PrivateRoute>
                                    <UserAssetsPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/register-user"
                            element={
                                <PrivateRoute>
                                    <RegisterUserPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/edit-user/:id"
                            element={
                                <PrivateRoute>
                                    <EditUserPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/desktop-releases"
                            element={
                                <PrivateRoute>
                                    <DesktopReleasesAdminPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/desktop-releases/new"
                            element={
                                <PrivateRoute>
                                    <NewDesktopReleasePage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/desktop-releases/:id/edit"
                            element={
                                <PrivateRoute>
                                    <EditDesktopReleasePage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/tutorial-videos"
                            element={
                                <PrivateRoute>
                                    <TutorialVideosAdminPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/tutorial-videos/new"
                            element={
                                <PrivateRoute>
                                    <RegisterTutorialVideoPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/tutorial-videos/:id/edit"
                            element={
                                <PrivateRoute>
                                    <EditTutorialVideoPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/printer-models"
                            element={
                                <PrivateRoute>
                                    <ListPrinterModelsPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/printer-models/new"
                            element={
                                <PrivateRoute>
                                    <NewPrinterModelPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/printer-models/:id/edit"
                            element={
                                <PrivateRoute>
                                    <EditPrinterModelPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/subscription-plans"
                            element={
                                <PrivateRoute>
                                    <ListSubscriptionPlansPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/subscription-plans/new"
                            element={
                                <PrivateRoute>
                                    <NewSubscriptionPlanPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/subscription-plans/:id/edit"
                            element={
                                <PrivateRoute>
                                    <EditSubscriptionPlanPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/tickets"
                            element={
                                <PrivateRoute>
                                    <TicketsListPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/tickets/new"
                            element={
                                <PrivateRoute>
                                    <NewTicketPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/tickets/:id"
                            element={
                                <PrivateRoute>
                                    <TicketDetailPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/mp-subscriptions"
                            element={
                                <PrivateRoute>
                                    <MpSubscriptionsListPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/subscription-billing-exemptions"
                            element={
                                <PrivateRoute>
                                    <SubscriptionBillingExemptionsPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/farm-link-changelog"
                            element={
                                <PrivateRoute>
                                    <FarmLinkRoute>
                                        <FarmLinkChangelogPage />
                                    </FarmLinkRoute>
                                </PrivateRoute>
                            }
                        />
                    </Routes>
                </Container>
            </div>
        </ThemeProvider>
    );
}

function App() {
    return (
        <Router>
            <ScrollToTop />
            <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/termos-de-uso" element={<TermsOfUsePage />} />
                <Route path="/politica-de-privacidade" element={<PrivacyPolicyPage />} />
                <Route path="/activate-account" element={<AccountConfirmation />} />
                <Route path="/redefine-password" element={<PasswordRedefinition />} />
                <Route path="/admin/*" element={<AdminRoutes />} />
                <Route path="/calculator" element={<CalculatorPageFront />} />
            </Routes>
        </Router>
    );
}

export default App;
